import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { Badge, Bandeau, Carte, Champ, Modale, Squelette, Vide } from '../components/ui';
import { centimes, dateFr, isoJour, montant, nomClient } from '../lib/format';
import type { Vehicule } from '../api/types';

interface Amende {
  _id: string; dateInfraction: string; montantMinor: number; numeroPV?: string;
  description?: string; statut: string;
  vehicle?: { marque: string; modele: string; immatriculation: string };
  customer?: { nom: string; prenom?: string; raisonSociale?: string; telephone?: string };
  reservation?: { numero: string };
}

/**
 * L'amende arrive deux mois plus tard : le systeme retrouve seul qui conduisait
 * a la date de l'infraction (article 07).
 */
export default function Amendes() {
  const [liste, setListe] = useState<Amende[] | null>(null);
  const [vehicules, setVehicules] = useState<Vehicule[]>([]);
  const [ouvert, setOuvert] = useState(false);
  const [f, setF] = useState({ vehicle: '', date: isoJour(new Date()), montant: '', pv: '', description: '' });
  const [resultat, setResultat] = useState<{ amende: Amende; reservation: { numero: string; customer?: unknown } | null } | null>(null);
  const [erreur, setErreur] = useState('');

  const charger = useCallback(() => { api.get<Amende[]>('/operations/amendes').then(setListe); }, []);
  useEffect(() => { charger(); api.get<Vehicule[]>('/vehicules').then(setVehicules); }, [charger]);
  if (!liste) return <Squelette lignes={6} />;

  async function creer() {
    setErreur('');
    try {
      const r = await api.post<typeof resultat>('/operations/amendes', {
        vehicle: f.vehicle, dateInfraction: f.date, montantMinor: centimes(Number(f.montant || 0)),
        numeroPV: f.pv || undefined, description: f.description || undefined,
      });
      setResultat(r); setOuvert(false);
      setF({ vehicle: '', date: isoJour(new Date()), montant: '', pv: '', description: '' });
      charger();
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : 'Erreur'); }
  }

  const aRefacturer = liste.filter((a) => a.statut === 'a_refacturer');
  const total = aRefacturer.reduce((a, x) => a + x.montantMinor, 0);

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>Amendes</h1>
          <p>Rattachees automatiquement au conducteur de la date — article 07</p>
        </div>
        <div className="actions"><button className="btn btn-p" onClick={() => setOuvert(true)}>Enregistrer une amende</button></div>
      </div>

      {resultat && (
        <div style={{ marginBottom: 14 }}>
          <Bandeau type={resultat.reservation ? 'ok' : 'warn'}
                   titre={resultat.reservation ? `Rattachee a ${resultat.reservation.numero}` : "Aucune location à cette date"}>
            {resultat.reservation
              ? `Le conducteur du jour a ete retrouve : ${nomClient(resultat.reservation.customer as never)}. L'amende est marquee « a refacturer ».`
              : "Aucun client ne conduisait ce véhicule à cette date : l'amende reste à la charge de l'agence."}
          </Bandeau>
        </div>
      )}
      {aRefacturer.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <Bandeau type="accent" titre={`${montant(total)} a refacturer a vos clients`}>
            {aRefacturer.length} amende(s) rattachee(s) a une location, pas encore repercutee(s).
          </Bandeau>
        </div>
      )}

      <Carte pad={false}>
        {liste.length === 0 ? <div className="carte-p"><Vide titre="Aucune amende enregistrée" /></div> : (
          <div className="enveloppe-tbl">
            <table className="tbl">
              <thead>
                <tr><th>Infraction</th><th>Véhicule</th><th>Conducteur retrouve</th><th>N° PV</th>
                    <th className="droite">Montant</th><th>Statut</th></tr>
              </thead>
              <tbody>
                {liste.map((a) => (
                  <tr key={a._id}>
                    <td className="num">{dateFr(a.dateInfraction)}<div className="mini sourdine">{a.description}</div></td>
                    <td><b>{a.vehicle?.marque} {a.vehicle?.modele}</b><div className="mini sourdine">{a.vehicle?.immatriculation}</div></td>
                    <td>{a.reservation ? <>{nomClient(a.customer)}<div className="mini sourdine">{a.reservation.numero}</div></> : <span className="sourdine mini">a la charge de l'agence</span>}</td>
                    <td className="mini sourdine">{a.numeroPV ?? '—'}</td>
                    <td className="droite montant">{montant(a.montantMinor)}</td>
                    <td><Badge statut={a.statut === 'a_refacturer' ? 'retour' : a.statut === 'refacturee' ? 'en_cours' : 'terminee'}
                               libelle={a.statut.replace(/_/g, ' ')} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Carte>

      {ouvert && (
        <Modale titre="Enregistrer une amende" onFermer={() => setOuvert(false)}>
          <p className="mini sourdine" style={{ marginBottom: 12 }}>
            Saisissez la date de l'infraction, pas celle de réception : c'est elle qui permet de retrouver le conducteur.
          </p>
          <div style={{ display: 'grid', gap: 12 }}>
            <Champ label="Véhicule">
              <select value={f.vehicle} onChange={(e) => setF({ ...f, vehicle: e.target.value })}>
                <option value="">Choisir…</option>
                {vehicules.map((v) => <option key={v._id} value={v._id}>{v.marque} {v.modele} — {v.immatriculation}</option>)}
              </select>
            </Champ>
            <div className="grille g2">
              <Champ label="Date de l'infraction"><input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Champ>
              <Champ label="Montant (DZD)"><input type="number" value={f.montant} onChange={(e) => setF({ ...f, montant: e.target.value })} /></Champ>
            </div>
            <Champ label="N° du procès-verbal"><input value={f.pv} onChange={(e) => setF({ ...f, pv: e.target.value })} /></Champ>
            <Champ label="Description"><input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Excès de vitesse — RN5" /></Champ>
          </div>
          {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setOuvert(false)}>Annuler</button>
            <button className="btn btn-p" disabled={!f.vehicle || !f.montant} onClick={() => void creer()}>Enregistrer et rattacher</button>
          </div>
        </Modale>
      )}
    </>
  );
}
